import Input from '@/components/Input';
import { MagnifyingGlass } from '@phosphor-icons/react';
import { useState } from 'react';
import { ClickAwayListener } from '@mui/material';
import { twJoin } from 'tailwind-merge';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import highlightMatchedText from '@/features/reports/utils/highlightMatchedText';
import useGetIntegartionLogos from '@/features/reports/api/getIntegrationLogos';
import find from 'lodash/find';
import Tooltip from '@/components/Tooltip';

export default function Search({ tables, setTableIndex, setColumnIndex }) {
  const [inputValue, setInputValue] = useState('');
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  const { data } = useGetIntegartionLogos({
    config: {
      staleTime: Infinity,
    },
  });

  const logos = data?.integrations || [];

  const filteredTables = tables.filter((table) =>
    table.tableLabel.toLowerCase().includes(inputValue.toLowerCase())
  );

  const columns = tables.map((table) => table.columns).flat();

  const filteredColumns = columns.filter((column) =>
    column.columnLabel.toLowerCase().includes(inputValue.toLowerCase())
  );

  return (
    <div className="relative ml-auto w-2/5 h-8">
      <ClickAwayListener onClickAway={() => setOpen(false)}>
        <div
          className={twJoin('w-full absolute bg-white', open && 'shadow-popup')}
        >
          <Input
            type="text"
            placeholder="Search Tables/Columns"
            showClearInput
            value={inputValue}
            className={{
              wrapper: 'h-8',
              input: {
                wrapper: twJoin(
                  'p-2 h-full',
                  open && 'rounded-b-none border-b-0 !border-brand-ai-100'
                ),
                root: 'peer ml-2 order-2 flex-1 text-xs',
                clear: 'order-3',
              },
            }}
            rightElem={
              <MagnifyingGlass
                size={16}
                className="text-pv-text-secondary-text peer-focus:text-pv-neutral-grey-600 order-1"
              />
            }
            onChange={(e) => {
              setInputValue(e.target.value);
              if (e.target.value) {
                setOpen(true);
              } else {
                setOpen(false);
              }
            }}
            onFocus={() => {
              if (inputValue) {
                setOpen(true);
              }
            }}
          />
          {open && (
            <div className="border border-brand-ai-100 border-t-0 rounded-b-lg overflow-hidden">
              <Tabs
                value={activeTab}
                aria-label="search tabs"
                onChange={(e, newValue) => {
                  e.stopPropagation();
                  setActiveTab(newValue);
                }}
                classes={{
                  root: 'px-3 border-b border-b-pv-neutral-grey-100 !min-h-[35px]',
                  indicator: '!bg-brand-ai-100',
                }}
              >
                <Tab
                  label={
                    <div className="flex items-center gap-2.5">
                      <div>Tables</div>
                      <div className="text-[10px] leading-[16px] px-1.5 rounded-md bg-pv-neutral-grey-100 text-pv-text-secondary-text group-aria-[selected=true]:bg-brand-ai-100 group-aria-[selected=true]:text-white">
                        {filteredTables.length}
                      </div>
                    </div>
                  }
                  id="search-tab-0"
                  aria-controls="search-tabpanel-0"
                  classes={{
                    root: 'group px-4 !py-2 !min-h-7 !max-h-8 !font-normal !leading-[19px] !text-pv-text-primary-text !text-xs !normal-case hover:!text-brand-ai-100 aria-[selected=true]:!text-brand-ai-100 aria-[selected=true]:!font-medium',
                  }}
                  disableRipple
                />
                <Tab
                  label={
                    <div className="flex items-center gap-2.5">
                      <div>Columns</div>
                      <div className="text-[10px] leading-[16px] px-1.5 rounded-md bg-pv-neutral-grey-100 text-pv-text-secondary-text group-aria-[selected=true]:bg-brand-ai-100 group-aria-[selected=true]:text-white">
                        {filteredColumns.length}
                      </div>
                    </div>
                  }
                  id="search-tab-1"
                  aria-controls="search-tabpanel-1"
                  classes={{
                    root: 'group px-4 !py-2 !min-h-7 !max-h-8 !font-normal !leading-[19px] !text-pv-text-primary-text !text-xs !normal-case hover:!text-brand-ai-100 aria-[selected=true]:!text-brand-ai-100 aria-[selected=true]:!font-medium',
                  }}
                  disableRipple
                />
              </Tabs>
              <div
                className="m-2 max-h-[225px] overflow-y-auto"
                role="search-tabpanel"
                id={`search-tabpanel-${activeTab}`}
                aria-labelledby={`search-tab-${activeTab}`}
              >
                {((activeTab === 0 && filteredTables.length === 0) ||
                  (activeTab === 1 && filteredColumns.length === 0)) && (
                  <div className="text-xs text-pv-text-secondary-text mx-2">
                    No results found. Try refining your search.
                  </div>
                )}
                <>
                  {activeTab === 0 ? (
                    <>
                      {filteredTables.map((table, key) => (
                        <div
                          key={`${table.tableId}-${key}`}
                          className="px-2 py-1 flex items-center gap-1 justify-between cursor-pointer rounded hover:bg-pv-primary-primary-50"
                          onClick={() => {
                            setTableIndex(table.tableIndex);
                            setColumnIndex(0);
                            setOpen(false);
                          }}
                        >
                          <Tooltip
                            title={table.tableLabel}
                            placement="top"
                            displayTooltipOnOverflow
                          >
                            <div
                              className="text-pv-text-primary-text text-xs leading-[19px]"
                              dangerouslySetInnerHTML={{
                                __html: highlightMatchedText(
                                  table.tableLabel,
                                  inputValue
                                ),
                              }}
                            />
                          </Tooltip>
                          <div className="flex items-center px-2 py-0.5 text-[10px] text-pv-text-secondary-text leading-[16px] gap-2">
                            <img
                              src={find(logos, { slug: table.platform })?.logo}
                              alt={table.platform}
                              className="w-4 h-4"
                            />
                            <div>{table.source}</div>
                          </div>
                        </div>
                      ))}
                    </>
                  ) : (
                    <>
                      {filteredColumns.map((column, key) => (
                        <div
                          key={`${column.tableId}-${column.columnName}-${key}`}
                          className="px-2 py-1 flex items-center gap-1 justify-between cursor-pointer rounded hover:bg-pv-primary-primary-50"
                          onClick={() => {
                            setTableIndex(column.tableIndex);
                            setColumnIndex(column.columnIndex);
                            setOpen(false);
                          }}
                        >
                          <div className="flex items-center text-pv-text-primary-text text-xs">
                            <Tooltip
                              title={column.columnLabel}
                              placement="top"
                              displayTooltipOnOverflow
                            >
                              <div
                                className="truncate leading-[19px]"
                                dangerouslySetInnerHTML={{
                                  __html: highlightMatchedText(
                                    column.columnLabel,
                                    inputValue
                                  ),
                                }}
                              />
                            </Tooltip>
                            <Tooltip
                              title={column.columnName}
                              placement="top"
                              displayTooltipOnOverflow
                            >
                              <div
                                className="w-fit max-w-[100px] ml-2 bg-pv-primary-primary-100 rounded-lg px-1.5 py-0.5 truncate"
                                dangerouslySetInnerHTML={{
                                  __html: highlightMatchedText(
                                    column.columnName,
                                    inputValue
                                  ),
                                }}
                              />
                            </Tooltip>
                          </div>
                          <Tooltip
                            title={column.tableName}
                            placement="top"
                            displayTooltipOnOverflow
                          >
                            <div className="text-[10px] text-pv-text-secondary-text leading-[16px] truncate">
                              {column.tableName}
                            </div>
                          </Tooltip>
                        </div>
                      ))}
                    </>
                  )}
                </>
              </div>
            </div>
          )}
        </div>
      </ClickAwayListener>
    </div>
  );
}
